import { EquipmentGroup } from '@/backend/models/equipmentGroup';
import { EquipmentGroupAssignment } from '@/backend/models/equipmentGroupAssignment';
import { EquipmentItem } from '@/backend/models/equipmentItem';
import {
  CreateEquipmentGroupAssignmentRequest,
  CreateEquipmentGroupRequest,
  EquipmentGroupResponse,
} from '@/types/equipmentGroupTypes';
import { EquipmentItemCreateData, EquipmentItemData, EquipmentListResponse, EquipmentResponse } from '@/types/equipmentTypes';
import { FusaReply } from '@/types/replyTypes';
import { create } from 'zustand';
import { EquipmentController } from '../controllers/equipmentController';
import { EquipmentGroupAssignmentController } from '../controllers/equipmentGroupAssignmentController';
import { EquipmentGroupController } from '../controllers/equipmentGroupController';

export type EquipmentStore = {
  resetEquipment: () => void;
  equipmentList: EquipmentItem[];
  equipmentGroups: EquipmentGroup[];
  equipmentGroupAssignments: EquipmentGroupAssignment[];
  fetchEquipmentForProcedureInstance: (procedureInstanceId: string) => Promise<EquipmentListResponse>;
  fetchEquipmentGroupsForProcedureInstance: (procedureInstanceId: string) => Promise<void>;
  fetchEquipmentGroupAssignmentsForProcedureInstance: (
    procedureInstanceId: string,
  ) => Promise<void>;
  createEquipment: (item: EquipmentItemCreateData) => Promise<EquipmentResponse>;
  updateEquipment: (item: EquipmentItemData) => Promise<EquipmentResponse>;
  deleteEquipment: (item: EquipmentItemData) => Promise<FusaReply>;
  handleUpdatedEquipment: (equipmentItems: EquipmentItemData[]) => void;
  fetchEquipmentItem: (id: string) => Promise<EquipmentResponse>;
  createEquipmentGroup: (body: CreateEquipmentGroupRequest) => Promise<EquipmentGroupResponse>;
  createEquipmentGroupAssignment: (body: CreateEquipmentGroupAssignmentRequest) => Promise<void>;
  deleteEquipmentGroupAssignment: (id: string) => Promise<FusaReply>;
  loadingEquipmentList: boolean;
  setLoadingEquipmentList: (loading: boolean) => void;
};

const initialState = {
  equipmentList: [],
  equipmentGroups: [],
  equipmentGroupAssignments: [],
  loadingEquipmentList: false,
};

export const useEquipmentStore = create<EquipmentStore>((set, get) => ({
  ...initialState,
  resetEquipment: () => {
    set(initialState);
  },
  setLoadingEquipmentList: (loading: boolean) => {
    set({ loadingEquipmentList: loading });
  },
  fetchEquipmentForProcedureInstance: async (procedureInstanceId: string) => {
    get().setLoadingEquipmentList(true);
    const equipmentController = new EquipmentController();
    try {
      const res = await equipmentController.readAllForProcedureInstance(procedureInstanceId);
      if (!res.errors) {
        set({ equipmentList: res.equipment.map((equipment) => new EquipmentItem(equipment)) });
      }
      return res;
    } finally {
      get().setLoadingEquipmentList(false);
    }
  },
  fetchEquipmentGroupsForProcedureInstance: async (procedureInstanceId: string) => {
    const res = await new EquipmentGroupController().readByProcedureInstanceId(procedureInstanceId);
    if (!res.errors && res.equipmentGroups) {
      set({ equipmentGroups: res.equipmentGroups.map((g) => new EquipmentGroup(g)) });
    }
  },
  fetchEquipmentGroupAssignmentsForProcedureInstance: async (procedureInstanceId: string) => {
    const res = await new EquipmentGroupAssignmentController().readByProcedureInstanceId(procedureInstanceId);
    if (!res.errors && res.equipmentGroupAssignments) {
      set({
        equipmentGroupAssignments: res.equipmentGroupAssignments.map(
          (a) => new EquipmentGroupAssignment(a),
        ),
      });
    }
  },
  createEquipment: async (item: EquipmentItemCreateData) => {
    const equipmentController = new EquipmentController();
    const response = equipmentController.create(item).then((res) => {
      if (!res.errors && res.equipment) {
        const { equipmentList } = get();
        set({ equipmentList: [...equipmentList, new EquipmentItem(res.equipment)] });
      }
      return res;
    });
    return response;
  },
  updateEquipment: async (item: EquipmentItemData) => {
    const equipmentController = new EquipmentController();
    const response = equipmentController.update(item).then((res) => {
      if (!res.errors && res.equipment) {
        get().handleUpdatedEquipment([res.equipment]);
      }
      return res;
    });
    return response;
  },
  handleUpdatedEquipment: (equipmentItems: EquipmentItemData[]) => {
    const updatedList = get().equipmentList.map(
      (equipment) => new EquipmentItem(equipmentItems.find((item) => item.id === equipment.id) || equipment),
    );
    set({ equipmentList: updatedList });
  },
  fetchEquipmentItem: async (id: string) => {
    const equipmentController = new EquipmentController();
    const response = await equipmentController.read(id);
    if (!response.errors && response.equipment) {
      get().handleUpdatedEquipment([response.equipment]);
    }
    return response;
  },
  deleteEquipment: (item: EquipmentItemData) => {
    const equipmentController = new EquipmentController();
    const response = equipmentController.delete(item.id).then((res) => {
      if (!res.errors) {
        const { equipmentList, equipmentGroupAssignments } = get();
        set({
          equipmentList: equipmentList.filter((equipment) => equipment.id !== item.id),
          equipmentGroupAssignments: equipmentGroupAssignments.filter((a) => a.equipmentId !== item.id),
        });
      }
      return res;
    });
    return response;
  },
  createEquipmentGroup: async (body: CreateEquipmentGroupRequest) => {
    const res = await new EquipmentGroupController().create(body);
    if (!res.errors && res.equipmentGroup) {
      set({ equipmentGroups: [...get().equipmentGroups, new EquipmentGroup(res.equipmentGroup)] });
    }
    return res;
  },
  createEquipmentGroupAssignment: async (body: CreateEquipmentGroupAssignmentRequest) => {
    const res = await new EquipmentGroupAssignmentController().create(body);
    if (!res.errors && res.equipmentGroupAssignment) {
      const without = get().equipmentGroupAssignments.filter((a) => a.equipmentId !== body.equipmentId);
      set({
        equipmentGroupAssignments: [...without, new EquipmentGroupAssignment(res.equipmentGroupAssignment)],
      });
    }
  },
  deleteEquipmentGroupAssignment: async (id: string) => {
    const controller = new EquipmentGroupAssignmentController();
    const res = await controller.delete(id);
    if (!res.errors) {
      set({
        equipmentGroupAssignments: get().equipmentGroupAssignments.filter((a) => a.id !== id),
      });
    }
    return res;
  },
}));
